/**
 * @license
 * Copyright 2025 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { bubbleUpOutputsIfNeeded } from "../bubble.js";
import { invokeDescriber } from "./invoke-describer.js";
import { FileSystemHandlerFactory } from "./file-system-handler-factory.js";
import { err, ok } from "../data/file-system/utils.js";
import { transformContents } from "../data/inflate-deflate.js";
import { baseURLFromContext } from "../loader/loader.js";
export { CapabilitiesManagerImpl };
function findHandler(handlerName, kits) {
    const handler = kits
        ?.flatMap((kit) => Object.entries(kit.handlers))
        .find(([name]) => name === handlerName)
        ?.at(1);
    return handler;
}
function getHandler(handlerName, context) {
    const handler = findHandler(handlerName, context.kits);
    if (!handler || typeof handler === "string") {
        throw new Error("Trying to get one of the non-core handlers");
    }
    const invoke = "invoke" in handler ? handler.invoke : handler;
    return (async (inputs, invocationPath) => {
        try {
            const result = await invoke(inputs, {
                ...context,
                invocationPath,
                descriptor: {
                    id: `${handlerName}-called-from-run-module`,
                    type: handlerName,
                },
            });
            return maybeUnwrapError(result);
        }
        catch (e) {
            return { $error: e.message };
        }
    });
}
function maybeUnwrapError(o) {
    if (!o)
        return o;
    if (!("$error" in o))
        return o;
    let { $error } = o;
    if ($error &&
        typeof $error === "object" &&
        "kind" in $error &&
        $error.kind === "error" &&
        "error" in $error) {
        const error = $error.error;
        $error = error.message;
    }
    return { ...o, $error };
}
function createOutputHandler(context) {
    return (async (allInputs, invocationPath) => {
        const schema = allInputs.schema;
        const descriptor = {
            id: "output-from-run-module",
            type: "output",
            configuration: {
                schema: {
                    ...schema,
                    behavior: ["bubble"],
                },
            },
        };
        const { $metadata, ...inputs } = allInputs;
        const metadata = $metadata;
        if (metadata) {
            descriptor.metadata = metadata;
        }
        const delivered = await bubbleUpOutputsIfNeeded(inputs, descriptor, context, invocationPath);
        return { delivered };
    });
}
function isBlobCapabilityAruments(inputs) {
    return (!!inputs &&
        typeof inputs === "object" &&
        "contents" in inputs &&
        "transform" in inputs);
}
function createBlobHandler(context) {
    return (async (inputs) => {
        if (!isBlobCapabilityAruments(inputs)) {
            return err(`Invalid blob arguments`);
        }
        const { contents, transform } = inputs;
        if (transform !== "persistent-temporary") {
            return err(`Only "persistent-temporary" transform is supported.`);
        }
        const { store } = context;
        if (!store) {
            return err(`DataStore is required to provide blob transform`);
        }
        const graphUrl = baseURLFromContext(context);
        if (!graphUrl) {
            return err(`Graph URL is required to provide blob transform`);
        }
        const transforming = await transformContents(store, contents, transform, graphUrl);
        if (!ok(transforming))
            return transforming;
        return { contents: transforming };
    });
}
function createDescribeHandler(context) {
    return (async (inputs, _invocationPath) => {
        const graphStore = context.graphStore;
        if (!graphStore) {
            return { $error: "Unable to describe: GraphStore is unavailable." };
        }
        if (typeof inputs.url !== "string") {
            return {
                $error: `Unable to describe: "${inputs.url}" is not a string`,
            };
        }
        const addResult = graphStore.addByURL(inputs.url, [], context);
        const mutable = await graphStore.getLatest(addResult.mutable);
        const inspectable = mutable.graphs.get(addResult.graphId);
        if (!inspectable) {
            return {
                $error: `Unable to describe: ${inputs.url}: is not inspectable`,
            };
        }
        if (addResult.moduleId) {
            const result = await invokeDescriber(addResult.moduleId, mutable, mutable.graph, inputs.inputs || {}, inputs.inputSchema, inputs.outputSchema, new CapabilitiesManagerImpl(context));
            if (!result) {
                return {
                    $error: `Unable to describe: ${addResult.moduleId} has no describer`,
                };
            }
            return result;
        }
        else {
            return inspectable.describe(inputs.inputs);
        }
    });
}
class CapabilitiesManagerImpl {
    context;
    constructor(context) {
        this.context = context;
    }
    createSpec() {
        try {
            if (this.context) {
                const fs = new FileSystemHandlerFactory(this.context.fileSystem);
                return {
                    fetch: getHandler("fetch", this.context),
                    secrets: getHandler("secrets", this.context),
                    invoke: getHandler("invoke", this.context),
                    output: createOutputHandler(this.context),
                    describe: createDescribeHandler(this.context),
                    query: fs.query(),
                    read: fs.read(),
                    write: fs.write(),
                    blob: createBlobHandler(this.context),
                };
            }
        }
        catch (e) {
            // eat error
            // TODO: Make sure this never happens. This will likely happen when
            // a misconfigured context is supplied, which is fine in most cases:
            // we just give you back no capabilities.
        }
        return CapabilitiesManagerImpl.dummies();
    }
    static #dummies;
    static dummies() {
        if (this.#dummies)
            return this.#dummies;
        this.#dummies = Object.fromEntries([
            "fetch",
            "secrets",
            "invoke",
            "output",
            "describe",
            "query",
            "read",
            "write",
            "blob",
        ].map((name) => {
            return [name, () => ({ $error: "Capability not available" })];
        }));
        return this.#dummies;
    }
}
//# sourceMappingURL=capabilities-manager.js.map